import * as Result from "@gordonb/result/result";
import { renderUrl } from "../../util/route.ts";
import { type Ulid } from "../../models/ulid.ts";
import { type Slug, SlugSchema } from "../../models/slug.ts";
import {
  type Note,
  type NoteArrayResult,
  NoteArrayResultSchema,
  type NoteOptionResult,
  NoteOptionResultSchema,
  type NoteResult,
  NoteResultSchema,
} from "../../models/note.ts";
import { toBearerToken } from "../../util/bearer-token.ts";
import { ResultSchemaOf } from "../../models/result.ts";
import {
  PROJECT_NOTE_API_URL,
  PROJECT_NOTES_API_URL,
} from "../client-config.ts";
import * as z from "zod/v4";

const DeleteResultSchema = ResultSchemaOf(
  z.object({ deleted: SlugSchema }),
  z.string(),
);

/**
 * Get a note by slug
 */
export const getNote = async (
  token: string | undefined,
  projectId: Ulid,
  slug: Slug,
): Promise<NoteOptionResult> => {
  if (!token) {
    return Result.err("Authorization token required");
  }

  const url = renderUrl({
    base: globalThis.location.origin,
    path: PROJECT_NOTE_API_URL,
    params: { slug, project_id: projectId },
  });

  try {
    const response = await fetch(url, {
      method: "GET",
      headers: {
        authorization: toBearerToken(token),
      },
    });
    const json = await response.json();
    return NoteOptionResultSchema.parse(json);
  } catch (error) {
    return Result.err((error as Error).message);
  }
};

export type SearchNotesParams = {
  query?: string;
  type?: string;
  confidence?: number;
  since?: number;
  limit?: number;
  includeHidden?: boolean;
};

/**
 * Search notes for a project
 */
export const searchNotes = async ({
  token = undefined,
  projectId,
  query = {},
}: {
  token?: string;
  projectId: Ulid;
  query?: SearchNotesParams;
}): Promise<NoteArrayResult> => {
  if (!token) {
    return Result.err("Authorization token required");
  }

  const url = renderUrl({
    base: globalThis.location.origin,
    path: PROJECT_NOTES_API_URL,
    params: { project_id: projectId },
    query,
  });

  try {
    const response = await fetch(url, {
      method: "GET",
      headers: {
        authorization: toBearerToken(token),
      },
    });
    const json = await response.json();
    return NoteArrayResultSchema.parse(json);
  } catch (error) {
    return Result.err((error as Error).message);
  }
};

/**
 * Upsert a note by slug
 */
export const upsertNote = async (
  token: string | undefined,
  projectId: Ulid,
  slug: Slug,
  note: Partial<Note>,
): Promise<NoteResult> => {
  if (!token) {
    return Result.err("Authorization token required");
  }

  const url = renderUrl({
    base: globalThis.location.origin,
    path: PROJECT_NOTE_API_URL,
    params: { slug, project_id: projectId },
  });

  try {
    const response = await fetch(url, {
      method: "PUT",
      headers: {
        "Content-Type": "application/json",
        authorization: toBearerToken(token),
      },
      body: JSON.stringify({ ...note, slug }),
    });
    const json = await response.json();
    return NoteResultSchema.parse(json);
  } catch (error) {
    return Result.err((error as Error).message);
  }
};

/**
 * Delete a note by slug
 */
export const deleteNote = async (
  token: string | undefined,
  projectId: Ulid,
  slug: Slug,
): Promise<z.infer<typeof DeleteResultSchema>> => {
  if (!token) {
    return Result.err("Authorization token required");
  }

  const url = renderUrl({
    base: globalThis.location.origin,
    path: PROJECT_NOTE_API_URL,
    params: { slug, project_id: projectId },
  });

  try {
    const response = await fetch(url, {
      method: "DELETE",
      headers: {
        authorization: toBearerToken(token),
      },
    });
    const json = await response.json();
    return DeleteResultSchema.parse(json);
  } catch (error) {
    return Result.err((error as Error).message);
  }
};
